import React, { useState, useEffect } from "react";
import { Form } from "antd";
import { useMercuryContext } from "../../context";
import { successfulNotification, failedNotification } from "../../common/utils/notifications";
import AddRole from "./AddRole";
import EditRole from "./EditRole";
import {
  Role,
  RoleRequestProps,
  UpdatedPermission,
  RoleFormValues,
} from "./RoleInterfaces";
import { handleApiError } from "common/utils/handleApiError";

const RoleRequest: React.FC<RoleRequestProps> = ({
  initialValues,
  refresh,
  applicationsList,
  dashboardsList,
  permissionsList = [],
  hasPermissions,
  mode = "add",
}) => {
  const [form] = Form.useForm<RoleFormValues>();
  const { umsClient, socket } = useMercuryContext();
  const [isOpen, setIsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const openDrawer = () => {
    setIsOpen(true);
    form.resetFields();
    if (initialValues) {
      form.setFieldsValue({
        nameValue: initialValues.name,
        descriptionValue: initialValues.description,
        customerApplicationValue: initialValues.customerApplication,
        isActiveValue: initialValues.isActive,
        dashboardsValue: initialValues.dashboards || [],
      });
    }
  };

  const closeDrawer = () => {
    setIsOpen(false);
    form.resetFields();
  };

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        nameValue: initialValues.name,
        descriptionValue: initialValues.description,
        customerApplicationValue: initialValues.customerApplication,
        isActiveValue: initialValues.isActive,
        dashboardsValue: initialValues.dashboards || [],
      });
    }
  }, [initialValues, form]);

  const submitRoleInternal = async (
    values: RoleFormValues,
    updatedPermissions: UpdatedPermission[],
    roleId?: string
  ) => {
    setSubmitting(true);
    try {
      const payload = {
        name: values.nameValue,
        description: values.descriptionValue,
        customerApplication: values.customerApplicationValue,
        isActive: values.isActiveValue ?? false,
        dashboards: values.dashboardsValue || [],
        permissions: updatedPermissions,
      };

      if (roleId) {
        await umsClient.patch(`/ums-v1/roles/${roleId}`, payload);
      } else {
        await umsClient.post("/ums-v1/roles", payload);
      }

      socket?.emit("updateUserDetails");

      closeDrawer();
      successfulNotification(
        `Role has been successfully ${roleId ? "updated" : "added"}`,
        "Please refresh your page if the update is not visible."
      );
      refresh();
    } catch (error: any) {
      handleApiError(error, `Failed to ${roleId ? "update" : "add"} role`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddRole = (values: RoleFormValues, updatedPermissions: UpdatedPermission[]) => {
    submitRoleInternal(values, updatedPermissions);
  };

  const handleEditRole = (values: RoleFormValues, id: string, updatedPermissions: UpdatedPermission[]) => {
    submitRoleInternal(values, updatedPermissions, id);
  };
  return (
    <>
      {mode === "add" ? (
        <AddRole
          handlers={{
            showNewRoleDrawer: openDrawer,
            closeNewRoleDrawer: closeDrawer,
            onFinishNewRole: handleAddRole,
            handleRefresh: refresh,
          }}
          values={{
            newRoleForm: form,
            visibleNewRoleDrawer: isOpen,
            permissionsList, // now correctly typed as Permission[]
            applicationsList,
            dashboardsList,
            hasPermissions
          }}
        />
      ) : (
        <EditRole
          handlers={{
            closeEditDrawer: closeDrawer,
            onFinishEditing: handleEditRole,
            openEditDrawer: openDrawer,
          }}
          values={{
            editingForm: form,
            editFormRef: form,
            visibleEditDrawer: isOpen,
            editing: { editingRow: initialValues as Role },
            permissionsList,
            applicationsList,
            dashboardsList,
            hasPermissions
          }}
        />
      )}
    </>
  );
};

export default RoleRequest;
